import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AdminHeader from "../../components/AdminHeader";
import { FullPageLoading } from "../../components/Loading";
import { Eyebrow, Fleuron, HeadingItalic } from "../../components/design";
import { adminGetChapter, type ChapterDetail } from "../../lib/api";

// Admin-scoped chapter reader. Reachable from AdminUserDetail's
// "Öffnen →" links so admins can read a customer's full chapter text
// without leaving the admin context.

export default function AdminChapterDetail() {
  const { phone = "", title = "" } = useParams<{ phone: string; title: string }>();
  const decodedPhone = decodeURIComponent(phone);
  const decodedTitle = decodeURIComponent(title);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chapter, setChapter] = useState<ChapterDetail | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const ch = await adminGetChapter(decodedPhone, decodedTitle);
        setChapter(ch);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Kapitel konnte nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    })();
  }, [decodedPhone, decodedTitle]);

  if (loading) return <FullPageLoading label="Lade Kapitel …" />;

  const paragraphs = (chapter?.content || "")
    .split(/\n\s*\n+/g)
    .map((p) => p.trim())
    .filter(Boolean);
  const titleClean = (chapter?.title || decodedTitle).replace(
    /^\d+\s*[·.\-:]?\s*/,
    "",
  );
  const chapterNum = parseInt((chapter?.title || decodedTitle).split(" ")[0], 10);

  return (
    <div className="paper-bg" style={{ minHeight: "100vh" }}>
      <AdminHeader />

      {/* Sticky admin context banner */}
      <div
        style={{
          background: "var(--oxblood)",
          color: "var(--paper-50)",
          padding: "10px 22px",
          fontFamily: "var(--f-ui)",
          fontSize: 13,
          position: "sticky",
          top: 0,
          zIndex: 50,
          boxShadow: "0 2px 8px rgba(43,26,15,0.18)",
        }}
      >
        <div
          className="desk-container-app"
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span>
            <strong
              style={{ textTransform: "uppercase", letterSpacing: "0.12em" }}
            >
              Admin-Vorschau
            </strong>{" "}
            · Kapitel von <span style={{ opacity: 0.85 }}>{decodedPhone}</span>
          </span>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <Link
              to={`/admin/users/${encodeURIComponent(decodedPhone)}`}
              style={{
                color: "var(--paper-50)",
                textDecoration: "underline",
                textUnderlineOffset: 4,
              }}
            >
              ← Zurück zur Detailansicht
            </Link>
            <Link
              to="/admin"
              style={{
                color: "var(--paper-50)",
                textDecoration: "underline",
                textUnderlineOffset: 4,
                opacity: 0.85,
              }}
            >
              Erzähler-Übersicht
            </Link>
          </div>
        </div>
      </div>

      <main
        style={{
          maxWidth: 720,
          margin: "0 auto",
          padding: "30px 22px 60px",
        }}
      >
        {error && (
          <div
            role="alert"
            style={{
              background: "rgba(184,52,40,0.08)",
              border: "1px solid rgba(184,52,40,0.30)",
              borderRadius: 8,
              padding: "12px 14px",
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 14,
              color: "var(--error)",
              marginBottom: 20,
            }}
          >
            {error}
          </div>
        )}

        {chapter && (
          <article
            style={{
              background: "var(--paper-50)",
              borderRadius: 3,
              padding: "32px 28px 28px",
              border: "1px solid rgba(43,26,15,0.10)",
              boxShadow: "0 16px 28px -10px rgba(43,26,15,0.18)",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: 18 }}>
              <Eyebrow>Kapitel</Eyebrow>
              <HeadingItalic size={28}>
                <em>{titleClean || `Kapitel ${chapterNum || ""}`}</em>
              </HeadingItalic>
              <Fleuron style={{ margin: "12px auto 0", maxWidth: 200 }} />
              <div
                style={{
                  fontFamily: "var(--f-body)",
                  fontStyle: "italic",
                  fontSize: 13,
                  color: "var(--ink-faint)",
                  marginTop: 8,
                }}
              >
                Sättigung {chapter.saturation_score || 0} %
                {chapter.exported && " · exportiert"}
              </div>
            </div>

            {paragraphs.length === 0 ? (
              <p
                style={{
                  fontFamily: "var(--f-body)",
                  fontStyle: "italic",
                  fontSize: 15,
                  color: "var(--ink-faint)",
                  textAlign: "center",
                  margin: "24px 0",
                }}
              >
                Dieses Kapitel hat noch keinen Inhalt.
              </p>
            ) : (
              paragraphs.map((p, i) => (
                <p
                  key={i}
                  className={i === 0 ? "dropcap" : undefined}
                  style={{
                    fontFamily: "var(--f-display)",
                    fontSize: 17,
                    lineHeight: 1.6,
                    color: "var(--ink)",
                    margin: "0 0 16px",
                    textAlign: "justify",
                  }}
                >
                  {p}
                </p>
              ))
            )}

            {chapter.summary && (
              <div
                style={{
                  marginTop: 24,
                  paddingTop: 18,
                  borderTop: "1px dashed rgba(43,26,15,0.18)",
                }}
              >
                <div
                  className="t-caps"
                  style={{ marginBottom: 6, color: "var(--ink-faint)" }}
                >
                  Zusammenfassung
                </div>
                <p
                  style={{
                    fontFamily: "var(--f-body)",
                    fontStyle: "italic",
                    fontSize: 14.5,
                    lineHeight: 1.5,
                    color: "var(--ink-soft)",
                    margin: 0,
                  }}
                >
                  {chapter.summary}
                </p>
              </div>
            )}

            {chapter.pending_goals && chapter.pending_goals.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div
                  className="t-caps"
                  style={{ marginBottom: 6, color: "var(--ink-faint)" }}
                >
                  Offene Fragen für den nächsten Anruf
                </div>
                <ul
                  style={{
                    paddingLeft: 18,
                    margin: 0,
                    fontFamily: "var(--f-body)",
                    fontSize: 14.5,
                    color: "var(--ink-soft)",
                  }}
                >
                  {chapter.pending_goals.map((g, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>
                      {g}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </article>
        )}
      </main>
    </div>
  );
}
